import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { Close as CloseIcon, KeyboardArrowDown as ChevronIcon, Lock as LockIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';

export interface FilterPillTrailingAction {
  onClick: () => void;
  /** Tooltip text. */
  title: string;
  ariaLabel: string;
}

export interface FilterPillProps {
  /** Field name shown before the value ("Brand", "Listing status"). */
  label: string;
  /** Current selection, already formatted for display. */
  valueLabel: string;
  /**
   * Dim grey at the field's quiet default, primary-tinted once a value is
   * actually applied — so a silent default can never hide rows invisibly.
   * Defaults to true for bars that only show applied filters.
   */
  active?: boolean;
  /** Inert pill: no menu, no hover, a lock glyph in front of the label. */
  locked?: boolean;
  open: boolean;
  onOpen: () => void;
  /** Rendered before the label, e.g. a brand mark when one value is selected. */
  leading?: React.ReactNode;
  /** Optional × cell: clear the value or remove the pill entirely. */
  trailing?: FilterPillTrailingAction | null;
  /** Callback ref for the value menu's popover anchor. */
  anchorRef: (element: HTMLElement | null) => void;
}

/** Pill height matches the 34px small controls that share a toolbar with it. */
export const FILTER_PILL_HEIGHT = 34;

/**
 * The presentational half of a filter pill: label + value opens a menu, an
 * optional trailing × clears or removes it. Both the product browser and the
 * staged-products console render their pills through this so the two bars
 * agree on height, radius, type and tint; each keeps its own thin wrapper
 * that knows its field types and value menu.
 */
export const FilterPill: React.FC<FilterPillProps> = ({
  label,
  valueLabel,
  active = true,
  locked = false,
  open,
  onOpen,
  leading,
  trailing,
  anchorRef,
}) => (
  <Box
    ref={anchorRef}
    sx={{
      display: 'inline-flex',
      alignItems: 'stretch',
      height: FILTER_PILL_HEIGHT,
      maxWidth: 320,
      borderRadius: 1,
      border: '1px solid',
      borderColor: (theme) => (active ? alpha(theme.palette.primary.main, 0.28) : theme.palette.divider),
      bgcolor: (theme) => (active ? alpha(theme.palette.primary.main, 0.07) : theme.palette.grey[50]),
      overflow: 'hidden',
    }}
  >
    <Box
      component="button"
      type="button"
      onClick={locked ? undefined : onOpen}
      disabled={locked}
      aria-haspopup="listbox"
      aria-expanded={open}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.875,
        minWidth: 0,
        px: 1.25,
        border: 'none',
        background: 'none',
        cursor: locked ? 'default' : 'pointer',
        font: 'inherit',
        fontSize: '0.8125rem',
        color: active ? 'primary.dark' : 'text.secondary',
        '&:hover': locked
          ? undefined
          : {
              bgcolor: (theme) => (active ? alpha(theme.palette.primary.main, 0.1) : theme.palette.grey[100]),
            },
      }}
    >
      {locked && <LockIcon sx={{ flex: '0 0 auto', fontSize: 14, color: 'text.disabled' }} />}
      {leading}
      <Box
        component="span"
        sx={{
          flex: '0 0 auto',
          color: (theme) => (active ? alpha(theme.palette.primary.dark, 0.72) : theme.palette.text.disabled),
        }}
      >
        {label}
      </Box>
      <Box
        component="span"
        sx={{ fontWeight: 600, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {valueLabel}
      </Box>
      {!locked && <ChevronIcon sx={{ flex: '0 0 auto', fontSize: 16, opacity: 0.7 }} />}
    </Box>
    {trailing && (
      <Tooltip title={trailing.title}>
        <Box
          component="button"
          type="button"
          onClick={trailing.onClick}
          aria-label={trailing.ariaLabel}
          sx={{
            display: 'grid',
            placeItems: 'center',
            width: 26,
            flex: '0 0 auto',
            border: 'none',
            borderLeft: '1px solid',
            borderLeftColor: (theme) => (active ? alpha(theme.palette.primary.main, 0.22) : theme.palette.divider),
            background: 'none',
            cursor: 'pointer',
            color: active ? 'primary.dark' : 'text.disabled',
            '&:hover': {
              bgcolor: (theme) => (active ? alpha(theme.palette.primary.main, 0.14) : theme.palette.grey[200]),
            },
          }}
        >
          <CloseIcon sx={{ fontSize: 14 }} />
        </Box>
      </Tooltip>
    )}
  </Box>
);
